/**
 * ContactDetailsPanel — right-side customer details for the Support Center.
 * Extracted from Support.js during the iter-238 refactor (Feb 2026).
 */
import React from 'react';
import {
  Info, User, Mail, Tag, StickyNote, Calendar, ExternalLink, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChannelIcon, StatusBadge, channelOf, formatDate } from './atoms';

export function ContactDetailsPanel({
  language, isAr, t,
  selectedContact, contactDetails,
  detailsDraft, setDetailsDraft,
  detailsSaving, saveContactDetails,
  setShowDetailsPanel,
}) {
  return (
    <aside className="w-[320px] border-s border-white/10 flex flex-col bg-black/20 overflow-y-auto">
      <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-black/40 backdrop-blur z-10">
        <div className="flex items-center gap-2 text-white">
          <Info className="h-4 w-4 text-[#d4af37]" />
          <span className="font-semibold">
            {language === 'ar' ? 'بيانات العميل' : 'Customer Details'}
          </span>
        </div>
        <button
          onClick={() => setShowDetailsPanel(false)}
          className="w-8 h-8 rounded-lg hover:bg-white/[0.05] flex items-center justify-center text-[#A1A1AA] hover:text-white"
          data-testid="close-details-panel-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Avatar + status */}
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#d4af37]/25 to-[#25D366]/20 border border-white/10 flex items-center justify-center mb-3 relative">
            <User className="h-10 w-10 text-white/80" />
            <span className="absolute -bottom-1 -end-1 w-7 h-7 rounded-full bg-[#0a0a0a] border border-white/10 flex items-center justify-center shadow">
              <ChannelIcon channel={channelOf(selectedContact)} size={16} />
            </span>
          </div>
          <p className="text-white font-semibold">
            {contactDetails?.contact?.name || selectedContact.name}
          </p>
          <p className="text-xs text-[#71717A] font-mono" dir="ltr">
            +{selectedContact.phone}
          </p>
          <div className="mt-2">
            <StatusBadge status={selectedContact.status || 'new'} isAr={isAr} />
          </div>
        </div>

        {/* Editable fields */}
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#71717A] mb-1 flex items-center gap-1">
              <User className="h-3 w-3" /> {language === 'ar' ? 'الاسم' : 'Name'}
            </label>
            <Input
              value={detailsDraft.name}
              onChange={(e) => setDetailsDraft({ ...detailsDraft, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white text-sm rounded-lg"
              data-testid="details-name-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#71717A] mb-1 flex items-center gap-1">
              <Mail className="h-3 w-3" /> {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
            </label>
            <Input
              value={detailsDraft.email}
              onChange={(e) => setDetailsDraft({ ...detailsDraft, email: e.target.value })}
              placeholder={language === 'ar' ? 'اختياري' : 'Optional'}
              className="bg-white/5 border-white/10 text-white text-sm rounded-lg"
              dir="ltr"
              data-testid="details-email-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#71717A] mb-1 flex items-center gap-1">
              <Tag className="h-3 w-3" /> {language === 'ar' ? 'وسوم (مفصولة بفاصلة)' : 'Tags (comma-separated)'}
            </label>
            <Input
              value={detailsDraft.tags}
              onChange={(e) => setDetailsDraft({ ...detailsDraft, tags: e.target.value })}
              placeholder={language === 'ar' ? 'مثال: مهم, تاجر' : 'e.g. important, merchant'}
              className="bg-white/5 border-white/10 text-white text-sm rounded-lg"
              data-testid="details-tags-input"
            />
            {(contactDetails?.contact?.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {contactDetails.contact.tags.map((tg) => (
                  <span
                    key={tg}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/30 text-[#d4af37]"
                  >
                    {tg}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#71717A] mb-1 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> {language === 'ar' ? 'ملاحظات داخلية' : 'Internal Notes'}
            </label>
            <textarea
              value={detailsDraft.notes}
              onChange={(e) => setDetailsDraft({ ...detailsDraft, notes: e.target.value })}
              rows={3}
              placeholder={language === 'ar' ? 'ملاحظات لا يراها العميل...' : 'Notes visible to admins only...'}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#606060] resize-none"
              data-testid="details-notes-input"
            />
          </div>

          <Button
            onClick={saveContactDetails}
            disabled={detailsSaving}
            className="w-full bg-[#d4af37] hover:bg-[#e6c353] text-black font-bold"
            data-testid="save-details-btn"
          >
            {detailsSaving
              ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
              : (language === 'ar' ? 'حفظ' : 'Save')}
          </Button>
        </div>

        {/* Meta section */}
        <div className="border-t border-white/10 pt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs">{language === 'ar' ? 'القناة' : 'Channel'}</span>
            <ChannelIcon channel={channelOf(selectedContact)} size={14} showLabel />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs">{language === 'ar' ? 'مسؤول عنه' : 'Assigned to'}</span>
            <span className="text-white text-xs">
              {selectedContact.assigned_name || (language === 'ar' ? 'غير معين' : 'Unassigned')}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {language === 'ar' ? 'بدأت' : 'Started'}
            </span>
            <span className="text-white text-xs">
              {formatDate(selectedContact.session_started_at || selectedContact.created_at, isAr, t)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs">
              {language === 'ar' ? 'إجمالي الرسائل' : 'Total messages'}
            </span>
            <span className="text-white text-xs font-mono">
              {contactDetails?.stats?.total_messages ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs">
              {language === 'ar' ? 'من العميل' : 'From customer'}
            </span>
            <span className="text-emerald-300 text-xs font-mono">
              {contactDetails?.stats?.incoming ?? 0}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-xs">
              {language === 'ar' ? 'من الفريق' : 'From team'}
            </span>
            <span className="text-sky-300 text-xs font-mono">
              {contactDetails?.stats?.outgoing ?? 0}
            </span>
          </div>
        </div>

        {/* Linked platform user (if phone matches) */}
        {contactDetails?.linked_user && (
          <div className="border-t border-white/10 pt-4">
            <p className="text-[10px] uppercase tracking-wide text-[#71717A] mb-2">
              {language === 'ar' ? 'حساب مرتبط في المنصة' : 'Linked platform account'}
            </p>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-3 space-y-1">
              <p className="text-white text-sm font-medium">
                {contactDetails.linked_user.name || contactDetails.linked_user.full_name || '—'}
              </p>
              {contactDetails.linked_user.email && (
                <p className="text-xs text-[#A1A1AA]" dir="ltr">{contactDetails.linked_user.email}</p>
              )}
              <p className="text-[10px] text-[#71717A]">
                {language === 'ar' ? 'المصدر' : 'Source'}: {contactDetails.linked_user.source}
              </p>
            </div>
          </div>
        )}

        {/* Direct WhatsApp shortcut */}
        <a
          href={`https://wa.me/${selectedContact.phone}`}
          target="_blank"
          rel="noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#25D366]/30 bg-[#25D366]/10 py-2 text-[#25D366] text-xs hover:bg-[#25D366]/15"
        >
          <ExternalLink className="h-3 w-3" />
          {language === 'ar' ? 'فتح في WhatsApp' : 'Open in WhatsApp'}
        </a>
      </div>
    </aside>
  );
}
